import { z } from 'zod';
import type { QueryClient } from '@tanstack/react-query';
import { showWebSocketNotification } from '@shared/utils/websocketNotificationApi';

import type { WebSocketEventRouter } from './router';
import type { WebSocketEnvelope } from './types';

const NotificationPayloadSchema = z
  .object({
    title: z.string().optional(),
    message: z.string().optional(),
  })
  .passthrough();

export interface CommonEventsDependencies {
  queryClient: QueryClient;
  translate: (key: string, params?: Record<string, unknown>) => string;
}

export function registerCommonWebSocketHandlers(
  router: WebSocketEventRouter,
  { queryClient, translate }: CommonEventsDependencies,
): () => void {
  const disposeNotification = router.on('notification', (event: WebSocketEnvelope) => {
    const result = NotificationPayloadSchema.safeParse(event.data ?? {});
    if (!result.success) {
      return;
    }

    const { title, message: messageText } = result.data;
    const content = title || messageText;
    if (content) {
      showWebSocketNotification('info', content, title, 5);
    }
    queryClient.invalidateQueries({ queryKey: ['notifications'] });
  });

  const disposeMessage = router.on('message', () => {
    queryClient.invalidateQueries({ queryKey: ['messages', 'unread'] });
    const messageText = translate('notifications.general.newClientMessage');
    if (messageText) {
      showWebSocketNotification('info', messageText);
    }
  });

  return () => {
    disposeNotification();
    disposeMessage();
  };
}
